/* © NXAPP Limited */
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  format, addMonths, subMonths, startOfMonth, endOfMonth,
  startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth,
  isFriday, isToday, nextFriday, isPast,
} from 'date-fns'
import { supabase } from '@/lib/supabase'
import { useAuthContext } from '@/context/AuthContext'
import {
  Plus, Mail, BarChart2, Trash2, CheckCircle, Clock,
  ChevronLeft, ChevronRight, CalendarDays, List, X,
  Calendar, Edit, RotateCcw, Send, Zap,
} from 'lucide-react'
import EmptyState from '@/components/ui/EmptyState'
import Spinner from '@/components/ui/Spinner'
import StatusBadge from '@/components/ui/StatusBadge'
import { formatDate, formatDateTime } from '@/lib/utils'
import type { IssueStatus } from '@/types/database'
import type { IssueRow } from '@/types/rows'
import { useOptimisticList } from '@/hooks/useOptimistic'
import { authedFetch } from '@/lib/authedFetch'

type IssueWithSegment = IssueRow & { segments: { name: string } | null; campaign_id?: string }

const PIPELINE: { status: IssueStatus; label: string; color: string }[] = [
  { status: 'DRAFT',            label: 'Draft',   color: 'bg-gray-200 text-gray-700' },
  { status: 'PENDING_APPROVAL', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { status: 'APPROVED',         label: 'Approved', color: 'bg-blue-100 text-blue-700' },
  { status: 'SCHEDULED',        label: 'Scheduled', color: 'bg-indigo-100 text-indigo-700' },
  { status: 'SENT',             label: 'Sent',     color: 'bg-green-100 text-green-700' },
]

const STATUS_DOT: Partial<Record<IssueStatus, string>> = {
  DRAFT:            'bg-gray-400',
  PENDING_APPROVAL: 'bg-amber-400',
  APPROVED:         'bg-blue-500',
  SCHEDULED:        'bg-indigo-500',
  SENDING:          'bg-orange-400',
  SENT:             'bg-green-500',
  FAILED:           'bg-red-400',
}

// ─── Friday Picker ─────────────────────────────────────────────────────────────

function SchedulePicker({ title, initial, onPick, onClose }: { title: string; initial?: string | null; onPick: (d: Date) => void; onClose: () => void }) {
  // Default: next Friday 09:00, or the existing schedule when rescheduling
  const defaultDate = initial ? new Date(initial) : (() => { const d = nextFriday(new Date()); d.setHours(9, 0, 0, 0); return d })()
  const toLocalInput = (d: Date) => {
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
  }
  const [value, setValue] = useState(toLocalInput(defaultDate))
  const picked = value ? new Date(value) : null
  const inPast = picked !== null && picked.getTime() <= Date.now()

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="label">Send date &amp; time</label>
            <input type="datetime-local" className="input" value={value} onChange={e => setValue(e.target.value)} />
            {inPast && <p className="mt-1 text-xs text-red-600">Pick a time in the future</p>}
          </div>
          {picked && !inPast && (
            <p className="text-xs text-gray-500 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              Sends {format(picked, 'EEEE d MMMM yyyy')} at {format(picked, 'HH:mm')} (your local time)
            </p>
          )}
        </div>
        <div className="px-5 py-4 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} className="btn-secondary flex-1 justify-center">Cancel</button>
          <button
            disabled={!picked || inPast}
            onClick={() => picked && onPick(picked)}
            className="btn-primary flex-1 justify-center disabled:opacity-40"
          >
            <Calendar className="w-4 h-4" /> Schedule
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Issue Action Sheet ─────────────────────────────────────────────────────────

function IssueActionSheet({
  issue,
  campaignId,
  autoSend,
  onClose,
  onTransition,
  onSchedule,
  onDelete,
  onSendNow,
}: {
  issue: IssueWithSegment
  campaignId: string
  autoSend: boolean
  onClose: () => void
  onTransition: (id: string, status: IssueStatus, scheduledAt?: string) => void
  onSchedule: (issue: IssueWithSegment) => void
  onDelete: (id: string) => void
  onSendNow: (id: string) => void
}) {
  const s = issue.status
  const isSent = s === 'SENT' || s === 'SENDING'
  const [sending, setSending] = useState(false)

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md mx-0 sm:mx-4 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900 leading-snug">{issue.subject}</p>
              <div className="flex items-center gap-2 mt-1.5">
                {(() => {
                  const ai = aiBadge(issue, autoSend)
                  return <StatusBadge status={issue.status} label={ai?.label} variant={ai?.variant} />
                })()}
                {issue.scheduled_at && (
                  <span className="flex items-center gap-1 text-xs text-gray-400">
                    <Clock className="w-3 h-3" />{formatDateTime(issue.scheduled_at)}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-700 rounded shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-2">
          {!isSent ? (
            <Link
              to={`/campaigns/${campaignId}/issues/${issue.id}/edit`}
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800"
            >
              <Edit className="w-4 h-4 text-gray-400" /> Edit in builder
            </Link>
          ) : (
            <>
              <Link
                to={`/campaigns/${campaignId}/issues/${issue.id}/edit`}
                onClick={onClose}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-amber-50 transition-colors text-sm font-medium text-amber-700"
              >
                <Edit className="w-4 h-4" /> Edit (fixes the web archive only)
              </Link>
              <a
                href={`/i/${issue.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800"
              >
                <Edit className="w-4 h-4 text-gray-400" /> View web archive
              </a>
            </>
          )}
          {s === 'DRAFT' && (
            <button onClick={() => { onTransition(issue.id, 'PENDING_APPROVAL'); onClose() }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-amber-50 transition-colors text-sm font-medium text-amber-700">
              <Send className="w-4 h-4" /> Submit for approval
            </button>
          )}
          {s === 'PENDING_APPROVAL' && (
            <button onClick={() => { onTransition(issue.id, 'APPROVED'); onClose() }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-blue-50 transition-colors text-sm font-medium text-blue-700">
              <CheckCircle className="w-4 h-4" /> Approve
            </button>
          )}
          {(s === 'APPROVED' || s === 'SCHEDULED') && (
            <button onClick={() => { onClose(); onSchedule(issue) }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-indigo-50 transition-colors text-sm font-medium text-indigo-700">
              <Calendar className="w-4 h-4" /> {s === 'SCHEDULED' ? 'Change schedule' : 'Schedule…'}
            </button>
          )}
          {(s === 'APPROVED' || s === 'SCHEDULED') && (
            <button
              disabled={sending}
              onClick={async () => {
                if (!confirm('Send this issue to its audience now?')) return
                setSending(true)
                await onSendNow(issue.id)
                setSending(false)
                onClose()
              }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-green-50 transition-colors text-sm font-medium text-green-700 disabled:opacity-50"
            >
              <Zap className="w-4 h-4" /> {sending ? 'Sending…' : 'Send now'}
            </button>
          )}
          {s === 'SENT' && (
            <Link
              to={`/campaigns/${campaignId}/issues/${issue.id}/stats`}
              onClick={onClose}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-800"
            >
              <BarChart2 className="w-4 h-4 text-gray-400" /> View stats
            </Link>
          )}
          {!isSent && s !== 'DRAFT' && (
            <button onClick={() => { onTransition(issue.id, 'DRAFT'); onClose() }}
              className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-gray-50 transition-colors text-sm font-medium text-gray-500">
              <RotateCcw className="w-4 h-4" /> Revert to draft
            </button>
          )}
          <button
            onClick={() => { if (confirm('Delete this issue? This cannot be undone.')) { onDelete(issue.id); onClose() } }}
            className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-red-50 transition-colors text-sm font-medium text-red-600">
            <Trash2 className="w-4 h-4" /> Delete issue
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Calendar ──────────────────────────────────────────────────────────────────

function IssueCalendar({
  issues, approvedIssues, onIssueClick, onScheduleDate,
}: {
  issues: IssueWithSegment[]
  approvedIssues: IssueWithSegment[]
  onIssueClick: (issue: IssueWithSegment) => void
  onScheduleDate: (date: Date, issueId?: string) => void
}) {
  const [current, setCurrent] = useState(new Date())
  const [datePickIssues, setDatePickIssues] = useState<IssueWithSegment[] | null>(null)
  const [pendingDate, setPendingDate] = useState<Date | null>(null)

  const monthStart = startOfMonth(current)
  const monthEnd   = endOfMonth(current)
  const calStart   = startOfWeek(monthStart, { weekStartsOn: 1 })
  const calEnd     = endOfWeek(monthEnd,     { weekStartsOn: 1 })
  const days       = eachDayOfInterval({ start: calStart, end: calEnd })

  const byDate: Record<string, IssueWithSegment[]> = {}
  for (const iss of issues) {
    if (!iss.scheduled_at) continue
    const key = format(new Date(iss.scheduled_at), 'yyyy-MM-dd')
    ;(byDate[key] ??= []).push(iss)
  }

  function handleDayClick(day: Date) {
    if (!isSameMonth(day, current)) return
    if ((isToday(day) || !isPast(day)) && approvedIssues.length > 0) {
      if (approvedIssues.length === 1) { onScheduleDate(day) }
      else { setPendingDate(day); setDatePickIssues(approvedIssues) }
    }
  }

  return (
    <>
      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <button onClick={() => setCurrent(subMonths(current, 1))} className="btn-secondary p-2"><ChevronLeft className="w-4 h-4" /></button>
          <h2 className="font-semibold text-gray-900">{format(current, 'MMMM yyyy')}</h2>
          <button onClick={() => setCurrent(addMonths(current, 1))} className="btn-secondary p-2"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 border-b border-gray-100 bg-gray-50">
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(h => (
            <div key={h} className={`py-2 text-center text-xs font-semibold ${h === 'Fri' ? 'text-brand-600' : 'text-gray-400'}`}>{h}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map(day => {
            const key       = format(day, 'yyyy-MM-dd')
            const dayIssues = byDate[key] ?? []
            const inMonth   = isSameMonth(day, current)
            const friday    = isFriday(day)
            const today     = isToday(day)
            const canSched  = (today || !isPast(day)) && inMonth && approvedIssues.length > 0
            return (
              <div
                key={key}
                onClick={() => handleDayClick(day)}
                className={[
                  'min-h-[88px] p-1.5 border-b border-r border-gray-100 flex flex-col gap-1',
                  !inMonth && 'bg-gray-50/60 opacity-50',
                  friday && inMonth && 'bg-blue-50/40',
                  canSched && 'cursor-pointer hover:bg-blue-100/60 transition-colors',
                ].filter(Boolean).join(' ')}
              >
                <span className={[
                  'text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full select-none',
                  today ? 'bg-brand-600 text-white' : friday && inMonth ? 'text-brand-700 font-bold' : 'text-gray-400',
                ].filter(Boolean).join(' ')}>
                  {format(day, 'd')}
                </span>
                {dayIssues.map(iss => (
                  <button
                    key={iss.id}
                    onClick={e => { e.stopPropagation(); onIssueClick(iss) }}
                    title={iss.subject}
                    className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-white border border-gray-200 shadow-sm hover:border-brand-400 hover:shadow-md transition-all text-left w-full"
                  >
                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${STATUS_DOT[iss.status] ?? 'bg-gray-400'}`} />
                    <span className="truncate text-gray-700">{iss.subject}</span>
                  </button>
                ))}
                {canSched && dayIssues.length === 0 && (
                  <div className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] text-brand-400 border border-dashed border-brand-200 pointer-events-none">
                    <Plus className="w-2.5 h-2.5 shrink-0" /> Schedule
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <div className="flex flex-wrap items-center gap-4 px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
          {Object.entries({ Scheduled: 'bg-indigo-500', Approved: 'bg-blue-500', Sent: 'bg-green-500' }).map(([label, cls]) => (
            <span key={label} className="flex items-center gap-1.5"><span className={`w-2 h-2 rounded-full ${cls}`} />{label}</span>
          ))}
          {approvedIssues.length > 0 && (
            <span className="ml-auto text-brand-600 font-medium">{approvedIssues.length} approved — click a future day to schedule (09:00)</span>
          )}
        </div>
      </div>

      {pendingDate && datePickIssues && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <div>
                <h2 className="font-semibold text-gray-900">Schedule for {format(pendingDate, 'EEE d MMM')}</h2>
                <p className="text-xs text-gray-500 mt-0.5">Which issue?</p>
              </div>
              <button onClick={() => { setPendingDate(null); setDatePickIssues(null) }} className="p-1 text-gray-400 hover:text-gray-700 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-4 space-y-2">
              {datePickIssues.map(iss => (
                <button
                  key={iss.id}
                  onClick={() => { onScheduleDate(pendingDate, iss.id); setPendingDate(null); setDatePickIssues(null) }}
                  className="w-full text-left px-4 py-3 rounded-xl border border-gray-200 hover:border-brand-400 hover:bg-brand-50 transition-colors text-sm font-medium text-gray-800"
                >
                  {iss.subject}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Main exported content component ───────────────────────────────────────────

/**
 * When the campaign has auto-send on, a dated draft is really "Scheduled by AI"
 * (the failsafe will send it), and an issue the AI sent reads "Sent by AI".
 */
// Every pre-send state the failsafe will auto-send (mirror of PRE_SEND_STATES).
const AI_PRESEND: IssueStatus[] = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED']

function aiBadge(
  issue: IssueWithSegment,
  autoSend: boolean,
): { label: string; variant: string } | null {
  if (autoSend && AI_PRESEND.includes(issue.status) && issue.scheduled_at)
    return { label: 'Scheduled by AI', variant: 'SCHEDULED_AI' }
  const dj = issue.design_json as { autoSentByAi?: boolean } | null
  if (issue.status === 'SENT' && dj?.autoSentByAi)
    return { label: 'Sent by AI', variant: 'SENT_AI' }
  return null
}

export default function IssuesContent({
  campaignId,
  autoSend = false,
}: {
  campaignId: string
  autoSend?: boolean
}) {
  const { user } = useAuthContext()
  const qc = useQueryClient()
  const issuesKey = ['issues', user?.id, campaignId]
  const { optimisticUpdate, rollback } = useOptimisticList<IssueWithSegment>(issuesKey)

  const [tab, setTab] = useState<'list' | 'calendar'>('list')
  const [activeIssue, setActiveIssue] = useState<IssueWithSegment | null>(null)
  const [schedulingIssue, setSchedulingIssue] = useState<IssueWithSegment | null>(null)

  const { data: issues, isLoading } = useQuery({
    queryKey: issuesKey,
    queryFn: async () => {
      const { data } = await supabase
        .from('issues')
        .select('*, segments(name)')
        .eq('user_id', user!.id)
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false })
      return (data ?? []) as IssueWithSegment[]
    },
    enabled: !!user,
  })

  const transition = useMutation({
    mutationFn: async ({ id, status, scheduledAt }: { id: string; status: IssueStatus; scheduledAt?: string }) => {
      const update: Record<string, unknown> = { status }
      if (scheduledAt !== undefined) update.scheduled_at = scheduledAt || null
      const { error } = await supabase.from('issues').update(update).eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onMutate: async ({ id, status, scheduledAt }) => {
      const prev = await optimisticUpdate(old =>
        old.map(i => i.id === id ? { ...i, status, scheduled_at: scheduledAt ?? i.scheduled_at } : i)
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => rollback(ctx?.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: issuesKey }),
  })

  const deleteIssue = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('issues').delete().eq('id', id).eq('user_id', user!.id)
      if (error) throw error
    },
    onMutate: async (id) => {
      const prev = await optimisticUpdate(old => old.filter(i => i.id !== id))
      return { prev }
    },
    onError: (_e, _v, ctx) => rollback(ctx?.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: issuesKey }),
  })

  function doTransition(id: string, status: IssueStatus, scheduledAt?: string) {
    transition.mutate({ id, status, scheduledAt: status === 'DRAFT' ? '' : scheduledAt })
  }

  async function sendNow(id: string) {
    const res = await authedFetch('/api/send-issue', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ issueId: id }),
    })
    if (res.ok) {
      const r = await res.json().catch(() => null) as { sent?: number; remaining?: number; done?: boolean } | null
      if (r && r.done === false && (r.remaining ?? 0) > 0) {
        alert(`Sent ${r.sent} in this batch. ${r.remaining} remaining — they'll keep sending automatically every few minutes (batch size / daily cap on the sending profile).`)
      }
      qc.invalidateQueries({ queryKey: issuesKey })
    } else {
      alert(`Send failed: ${await res.text()}`)
    }
  }

  function handleSchedulePick(date: Date) {
    if (!schedulingIssue) return
    transition.mutate({ id: schedulingIssue.id, status: 'SCHEDULED', scheduledAt: date.toISOString() })
    setSchedulingIssue(null)
  }

  function handleCalendarScheduleDate(date: Date, issueId?: string) {
    const at = new Date(date)
    at.setHours(9, 0, 0, 0)   // calendar day-clicks schedule at 09:00 local
    const approved = (issues ?? []).filter(i => i.status === 'APPROVED')
    const target = issueId ?? (approved.length === 1 ? approved[0].id : null)
    if (target) transition.mutate({ id: target, status: 'SCHEDULED', scheduledAt: at.toISOString() })
  }

  const approvedIssues = (issues ?? []).filter(i => i.status === 'APPROVED')
  const counts = PIPELINE.reduce((acc, p) => {
    acc[p.status] = (issues ?? []).filter(i => i.status === p.status).length
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1">
          {([
            ['list',     <List className="w-3.5 h-3.5" />,        'List']     ,
            ['calendar', <CalendarDays className="w-3.5 h-3.5" />, 'Calendar'] ,
          ] as const).map(([t, icon, label]) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                tab === t ? 'bg-brand-600 text-white' : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {icon}{label}
            </button>
          ))}
        </div>
        <Link to={`/campaigns/${campaignId}/issues/new`} className="btn-primary">
          <Plus className="w-4 h-4" /> New issue
        </Link>
      </div>

      {/* Pipeline counts */}
      {!isLoading && (issues?.length ?? 0) > 0 && (
        <div className="grid grid-cols-5 gap-2">
          {PIPELINE.map(p => (
            <div key={p.status} className="card p-3 text-center">
              <p className="text-2xl font-bold text-gray-900">{counts[p.status] ?? 0}</p>
              <span className={`mt-1 inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full ${p.color}`}>{p.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* List */}
      {tab === 'list' && (
        <div className="card overflow-hidden">
          {isLoading ? <div className="flex justify-center py-12"><Spinner /></div>
          : !issues?.length ? (
            <EmptyState
              icon={Mail}
              title="No issues yet"
              description="Create your first issue for this campaign."
              action={<Link to={`/campaigns/${campaignId}/issues/new`} className="btn-primary">Create issue</Link>}
            />
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-medium text-gray-500 bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-3 text-left">Subject</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Scheduled</th>
                  <th className="px-4 py-3 text-left">Created</th>
                  <th className="px-4 py-3 w-8"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {issues.map(issue => (
                  <tr key={issue.id} onClick={() => setActiveIssue(issue)} className="hover:bg-gray-50 cursor-pointer">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {issue.subject}
                      <span className="ml-2 text-xs text-gray-400">
                        {(() => {
                          const a = (issue as { audience?: { campaignLeads?: boolean; allSubscribed?: boolean; segmentIds?: string[] } }).audience
                          if (a) {
                            if (a.allSubscribed) return 'All subscribed'
                            const parts = []
                            if (a.campaignLeads) parts.push('Campaign leads')
                            if (a.segmentIds?.length) parts.push(`${a.segmentIds.length} segment${a.segmentIds.length !== 1 ? 's' : ''}`)
                            return parts.join(' + ') || '⚠ no audience'
                          }
                          return issue.segments?.name ?? ''
                        })()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        const ai = aiBadge(issue, autoSend)
                        return <StatusBadge status={issue.status} label={ai?.label} variant={ai?.variant} />
                      })()}
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {issue.scheduled_at ? (
                        <span className="flex items-center gap-1 text-xs"><Clock className="w-3 h-3" />{formatDateTime(issue.scheduled_at)}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(issue.created_at)}</td>
                    <td className="px-4 py-3 text-gray-300 text-lg leading-none">›</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Calendar */}
      {tab === 'calendar' && (
        isLoading ? <div className="flex justify-center py-12"><Spinner /></div> : (
          <IssueCalendar
            issues={issues ?? []}
            approvedIssues={approvedIssues}
            onIssueClick={setActiveIssue}
            onScheduleDate={handleCalendarScheduleDate}
          />
        )
      )}

      {activeIssue && (
        <IssueActionSheet
          issue={activeIssue}
          campaignId={campaignId}
          autoSend={autoSend}
          onClose={() => setActiveIssue(null)}
          onTransition={doTransition}
          onSchedule={iss => { setActiveIssue(null); setSchedulingIssue(iss) }}
          onDelete={id => deleteIssue.mutate(id)}
          onSendNow={sendNow}
        />
      )}
      {schedulingIssue && (
        <SchedulePicker
          title={`Schedule: ${schedulingIssue.subject}`}
          initial={schedulingIssue.scheduled_at}
          onPick={handleSchedulePick}
          onClose={() => setSchedulingIssue(null)}
        />
      )}
    </div>
  )
}
